import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getAllPartners, deletePartner, getPartnerRequests, approvePartnerRequest, rejectPartnerRequest, sendPartnerInvitation } from "../../../../api/apiFunction/partnerServices";
import { DollarSign, Users, Link, Plus, Building2, User, Star, Trash2, Pencil, BarChart3, Search, Copy, CheckCircle, XCircle, Clock, X, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import AddOrUpdateModal from "./AddOrUpdateModal";

const PartnerApplication = ({ hideTitle }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true); // Start with true to show loader on mount
  const [editingPartner, setEditingPartner] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState({});
  const PAGE_SIZE = 10;
  
  const [activeTab, setActiveTab] = useState("create");
  const [filterType, setFilterType] = useState('all');
  const partnerTypeOptions = Array.from(new Set(partners.map(p => p.type).filter(Boolean)));
  const [partnerTypeFilter, setPartnerTypeFilter] = useState("");
  const [partnerRequests, setPartnerRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalData, setApprovalData] = useState({
    rewardType: "flat",
    rewardValue: "",
    userBonus: { type: "none", value: "" }, // none = no reward, flat = fixed amount, percentage = % discount
    totalSignuplimit: "",
    referralCode: ""
  });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [intervalActive, setIntervalActive] = useState(false);

  useEffect(() => {
    fetchPartners();
    if (activeTab === "requests") {
      fetchPartnerRequests();
    }
  }, [activeTab, currentPage, activeSearch]); // Add currentPage and activeSearch to dependencies

  // Auto-refresh partners every minute (aligned to minute + 3 seconds)
  useEffect(() => {
    console.log("🔄 Setting up minute+3s aligned auto-refresh for partners...");
    setIntervalActive(true);

    let intervalId;
    let timeoutId;

    const scheduleNext = () => {
      const now = new Date();
      const seconds = now.getSeconds();
      const milliseconds = now.getMilliseconds();

      // ms until next minute (hh:mm:00)
      const msUntilNextMinute = (60 - seconds) * 1000 - milliseconds;

      timeoutId = setTimeout(() => {
        // ⏱ extra 3 second delay
        setTimeout(() => {
          const callTime = new Date();
          console.log(
            "⏰ Refresh partners at (minute + 3s):",
            callTime.toLocaleTimeString(),
          );

          fetchPartners(false); // Don't show loading state for auto-refresh
          if (activeTab === "requests") {
            fetchPartnerRequests();
          }
          setLastRefresh(new Date());

          // Keep calling every minute at :03
          intervalId = setInterval(() => {
            fetchPartners(false); // Don't show loading state for auto-refresh
            if (activeTab === "requests") {
              fetchPartnerRequests();
            }
            setLastRefresh(new Date());
          }, 60000);
        }, 3000); // ✅ +3 second
      }, msUntilNextMinute);
    };

    scheduleNext();

    return () => {
      console.log("🔄 Clearing minute+3s refresh timers for partners");
      setIntervalActive(false);
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [activeTab]);

  // Refresh when page becomes visible (user switches back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log("🔄 Page became visible, refreshing partners...");
        fetchPartners(false); // Don't show loading state for visibility refresh
        if (activeTab === "requests") {
          fetchPartnerRequests();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [activeTab]);

  const fetchPartners = async (showLoadingState = true, page = currentPage, search = activeSearch) => {
    if (showLoadingState) {
      setLoading(true);
    }
    try {
      const params = {
        page,
        limit: PAGE_SIZE
      };
      
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      const res = await getAllPartners(params);
      
      // Handle response structure: data.partners for paginated, data for non-paginated
      if (res?.data?.partners) {
        setPartners(res.data.partners || []);
        setPagination(res.data.pagination || {});
        setTotalPages(res.data.pagination?.totalPages || 1);
        setLastRefresh(new Date());
      } else {
        // Fallback for non-paginated response
        setPartners(res?.data || []);
        setTotalPages(1);
        setLastRefresh(new Date());
      }
    } catch (error) {
      if (showLoadingState) {
        toast.error("Failed to fetch partners");
      }
      setPartners([]);
      setTotalPages(1);
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  // Handle filter button click
  const handleFilter = () => {
    setActiveSearch(searchTerm);
    setCurrentPage(1); // Reset to page 1 when filtering
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveSearch('');
    setCurrentPage(1);
  };

  const handleDeletePartner = async (partnerId) => {
    const partner = partners.find(p => p._id === partnerId);
    if (partner) {
      setPartnerToDelete(partner);
      setShowDeleteModal(true);
    }
  };

  const confirmDeletePartner = async () => {
    if (!partnerToDelete || deleteLoading) return;

    setDeleteLoading(true);
    try {
      await deletePartner(partnerToDelete._id);
      toast.success("Partner deleted successfully");
      setShowDeleteModal(false);
      setPartnerToDelete(null);
      fetchPartners();
    } catch (error) {
      toast.error("Failed to delete partner");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle opening modal for new partner
  const handleAddPartner = () => {
    setEditingPartner(null);
    setShowModal(true);
  };

  // Handle opening modal for editing partner
  const handleEditPartner = (partner) => {
    setEditingPartner(partner);
    setShowModal(true);
  };

  // Handle modal success (create/update)
  const handleModalSuccess = () => {
    fetchPartners();
    setShowModal(false);
    setEditingPartner(null);
  };

  // Handle send invitation email
  const handleSendInvitation = async () => {
    if (!inviteEmail || !inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }

    setInviteLoading(true);
    try {
      const response = await sendPartnerInvitation(inviteEmail.trim());
      console.log("Send invitation response:", response);
      
      // Check for successful response
      if (response?.status === 200 || (response?.data && !response?.data?.message?.includes("Failed"))) {
        toast.success(`Invitation email sent successfully to ${inviteEmail.trim()}`);
        setShowInviteModal(false);
        setInviteEmail("");
      } else {
        // Handle error response
        const errorMessage = response?.data?.message || response?.data?.error || "Failed to send invitation email. Please check server logs.";
        console.error("Email sending failed:", errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error sending invitation:", error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Failed to send invitation email. Please check your email configuration.";
      toast.error(errorMessage);
    } finally {
      setInviteLoading(false);
    }
  };

  // Fetch partner requests
  const fetchPartnerRequests = async () => {
    setRequestsLoading(true);
    try {
      const res = await getPartnerRequests();
      if (res?.data) {
        setPartnerRequests(res.data);
      }
    } catch (error) {
      toast.error("Failed to fetch partner requests");
      setPartnerRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  };

  // Handle approve request
  const handleApproveRequest = async () => {
    if (!approvalData.rewardValue || approvalData.rewardValue <= 0) {
      toast.error("Partner reward value is required and must be greater than 0");
      return;
    }

    if (approvalLoading) return; // Prevent multiple clicks

    setApprovalLoading(true);

    try {
      // Validate user bonus if type is not "none"
      if (approvalData.userBonus.type !== 'none' && (!approvalData.userBonus.value || parseFloat(approvalData.userBonus.value) <= 0)) {
        toast.error(`User ${approvalData.userBonus.type === 'flat' ? 'reward amount' : 'discount percentage'} must be greater than 0 if reward type is not "No Reward"`);
        setApprovalLoading(false);
        return;
      }

      const payload = {
        rewardType: "flat", // Always fixed amount per requirements
        rewardValue: parseFloat(approvalData.rewardValue),
        userBonus: {
          type: approvalData.userBonus.type,
          value: approvalData.userBonus.type === 'none' ? 0 : parseFloat(approvalData.userBonus.value) || 0
        },
        totalSignuplimit: parseInt(approvalData.totalSignuplimit) || 0,
        referralCode: approvalData.referralCode || undefined,
        adminNotes: adminNotes
      };

      const response = await approvePartnerRequest(selectedRequest._id, payload);
      if (response?.status === 200 || response?.status === 201) {
        toast.success("Partner request approved and partner created successfully!");
        setShowApprovalModal(false);
        setSelectedRequest(null);
        setAdminNotes("");
        setApprovalData({
          rewardType: "flat",
          rewardValue: "",
          userBonus: { type: "none", value: "" }, // Reset to no reward
          totalSignuplimit: "",
          referralCode: ""
        });
        fetchPartnerRequests();
        fetchPartners();
      } else {
        throw new Error(response?.data?.message || "Failed to approve request");
      }
    } catch (error) {
      toast.error(error.message || "Failed to approve partner request");
    } finally {
      setApprovalLoading(false);
    }
  };

  // Handle reject request
  const handleRejectRequest = async () => {
    try {
      const response = await rejectPartnerRequest(selectedRequest._id, { adminNotes });
      if (response?.status === 200) {
        toast.success("Partner request rejected");
        setShowRejectionModal(false);
        setSelectedRequest(null);
        setAdminNotes("");
        fetchPartnerRequests();
      } else {
        throw new Error(response?.data?.message || "Failed to reject request");
      }
    } catch (error) {
      toast.error(error.message || "Failed to reject partner request");
    }
  };

  // Open approval modal
  const openApprovalModal = (request) => {
    setSelectedRequest(request);
    setApprovalData({
      rewardType: "flat", // Always fixed amount per requirements
      rewardValue: "",
      userBonus: { type: "none", value: "" }, // Default: no user reward
      totalSignuplimit: "",
      referralCode: ""
    });
    setAdminNotes("");
    setShowApprovalModal(true);
  };

  // Open rejection modal
  const openRejectionModal = (request) => {
    setSelectedRequest(request);
    setAdminNotes("");
    setShowRejectionModal(true);
  };

  // Open details modal
  const openDetailsModal = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  // Filter requests
  const filteredRequests = partnerRequests.filter(request => {
    const matchesSearch = request.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.organization?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const pendingRequests = filteredRequests.filter(r => r.status === "pending");
  const approvedRequests = filteredRequests.filter(r => r.status === "approved");
  const rejectedRequests = filteredRequests.filter(r => r.status === "rejected");

  // Filter partners based on type only (search is handled by backend)
  const filteredPartners = partners.filter(partner => {
    const matchesType = filterType === 'all' || partner.type === filterType;
    const matchesPartnerType = !partnerTypeFilter || partner.type === partnerTypeFilter;
    return matchesType && matchesPartnerType;
  });

  // Helper function to get partner type icon
  const getPartnerTypeIcon = (type) => {
    switch (type) {
      case "university": return Building2;
      case "company": return Building2;
      case "celebrity": return Star;
      default: return User;
    }
  };

  // Helper function to format currency
  const formatCurrency = (value) => {
    return `$${(value || 0).toFixed(2)}`;
  };

  // Skeleton screen
  if (loading) {
    return (
      <div className="h-full p-6 overflow-y-auto custom-scrollbar">
        <div className="max-w-7xl mx-auto">
          {/* Tab Navigation Skeleton */}
          <div className="flex gap-2 mb-6 animate-pulse">
            <div className="h-8 w-24 bg-bg-40 rounded-lg" />
            <div className="h-8 w-24 bg-bg-40 rounded-lg" />
            <div className="h-8 w-24 bg-bg-40 rounded-lg" />
          </div>

          {/* Search and Filter Controls Skeleton */}
          <div className="flex items-end gap-3 mb-4 animate-pulse">
            {/* Search input skeleton */}
            <div className="flex-1 min-w-[200px]">
              <div className="h-4 w-16 bg-bg-40 rounded mb-2" />
              <div className="h-9 w-full bg-bg-40 rounded" />
            </div>
            {/* Filter button skeleton */}
            <div>
              <div className="h-9 w-20 bg-bg-40 rounded" />
            </div>
            {/* Clear button skeleton */}
            <div>
              <div className="h-9 w-16 bg-bg-40 rounded" />
            </div>
            {/* Type dropdown skeleton */}
            <div>
              <div className="h-4 w-12 bg-bg-40 rounded mb-2" />
              <div className="h-9 w-24 bg-bg-40 rounded" />
            </div>
            {/* Action buttons skeleton */}
            <div className="flex items-center gap-2">
              <div className="h-9 w-36 bg-bg-40 rounded" />
              <div className="h-9 w-28 bg-bg-40 rounded" />
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-bd-50">
                <thead className="bg-bg-40">
                  <tr>
                    {[...Array(10)].map((_, i) => (
                      <th key={i} className="px-6 py-3 text-left">
                        <div className="h-4 w-20 bg-bg-40 rounded animate-pulse"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-bg-60 divide-y divide-bd-50">
                  {[...Array(6)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(10)].map((_, j) => (
                        <td key={j} className="px-6 py-4 whitespace-nowrap">
                          <div className="h-6 w-24 bg-bg-40 rounded animate-pulse"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Skeleton */}
          <div className="flex justify-center items-center gap-1 mt-4 animate-pulse">
            <div className="h-8 w-8 bg-bg-40 rounded" />
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-8 w-8 bg-bg-40 rounded" />
            ))}
            <div className="h-8 w-8 bg-bg-40 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-6 overflow-y-auto custom-scrollbar">
      <div className="max-w-7xl mx-auto">

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            className={`py-1.5 px-4 text-sm font-medium transition-colors duration-150 border-none outline-none focus:outline-none rounded-lg ${
              activeTab === "create"
                ? "bg-bg-40 text-fg-90 border-b-2 border-ac-02 shadow-sm"
                : "bg-transparent text-fg-60 hover:text-fg-80"
            }`}
            style={{
              boxShadow:
                activeTab === "create" ? "0 1px 4px 0 rgba(0,0,0,0.03)" : "none",
            }}
            onClick={() => setActiveTab("create")}
          >
            Partners
          </button>
          <button
            className={`py-1.5 px-4 text-sm font-medium transition-colors duration-150 border-none outline-none focus:outline-none rounded-lg relative ${
              activeTab === "requests"
                ? "bg-bg-40 text-fg-90 border-b-2 border-ac-02 shadow-sm"
                : "bg-transparent text-fg-60 hover:text-fg-80"
            }`}
            style={{
              boxShadow:
                activeTab === "requests" ? "0 1px 4px 0 rgba(0,0,0,0.03)" : "none",
            }}
            onClick={() => setActiveTab("requests")}
          >
            Requests
            {pendingRequests.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {pendingRequests.length}
              </span>
            )}
          </button>
          <button
            className={`py-1.5 px-4 text-sm font-medium transition-colors duration-150 border-none outline-none focus:outline-none rounded-lg ${
              activeTab === "analytics"
                ? "bg-bg-40 text-fg-90 border-b-2 border-ac-02 shadow-sm"
                : "bg-transparent text-fg-60 hover:text-fg-80"
            }`}
            style={{
              boxShadow:
                activeTab === "analytics" ? "0 1px 4px 0 rgba(0,0,0,0.03)" : "none",
            }}
            onClick={() => setActiveTab("analytics")}
          >
            Stats
          </button>
        </div>

        {/* Partners Tab Content */}
         {activeTab === "create" && (
           <>
             {/* Search and Filter Controls */}
             <div className="flex items-end gap-3 mb-4">
               {/* Search */}
               <div className="flex-1 min-w-[200px]">
                 <label className="block text-xs font-medium text-fg-60 mb-1">Search</label>
                 <input
                   type="text"
                   className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                   placeholder="Search by name or email..."
                   value={searchTerm}
                   onChange={(e) => setSearchTerm(e.target.value)}
                   onKeyPress={(e) => e.key === 'Enter' && handleFilter()}
                   disabled={loading}
                 />
               </div>
               {/* Filter button */}
               <div>
                 <button
                   onClick={handleFilter}
                   disabled={loading}
                   className="flex items-center gap-2 px-4 py-1.5 text-xs font-medium bg-ac-02 hover:bg-ac-01 text-white dark:text-black rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   <Search size={14} />
                   {loading ? 'Searching...' : 'Filter'}
                 </button>
               </div>
               {/* Clear button */}
               {searchTerm && (
                 <div>
                   <button
                     onClick={handleClearSearch}
                     disabled={loading}
                     className="flex items-center gap-2 px-4 py-1.5 text-xs font-medium bg-bg-50 hover:bg-bg-40 text-fg-50 border border-bd-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                   >
                     <X size={14} />
                     Clear
                   </button>
                 </div>
               )}
               {/* Type Filter */}
               <div>
                 <label className="block text-xs font-medium text-fg-60 mb-1">Type</label>
                 <select
                   className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                   value={partnerTypeFilter}
                   onChange={(e) => setPartnerTypeFilter(e.target.value)}
                 >
                   <option value="">All</option>
                   <option value="university">University</option>
                   <option value="company">Company</option>
                   <option value="celebrity">Celebrity</option>
                 </select>
               </div>
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="bg-bg-50 text-fg-50 border border-bd-50 px-4 py-1.5 text-xs rounded-md hover:bg-bg-40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02 transition-colors flex items-center gap-2"
                >
                  <Mail size={14} />
                  Invite via Email
                </button>
                <button
                  onClick={handleAddPartner}
                  className="bg-ac-02 text-white dark:text-black px-4 py-1.5 text-xs rounded-md hover:bg-ac-01 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02 transition-colors flex items-center gap-2"
                >
                  <Plus size={14} />
                  Add Partner
                </button>
              </div>
            </div>

             {/* Basic Partners Table */}
             <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
               <div className="overflow-x-auto">
                 <table className="min-w-full divide-y divide-bd-50">
                   <thead className="bg-bg-40">
                     <tr>
                       <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                         Partner
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                         Type
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                         Reward
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                         User Bonus
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                         Signup Limit
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                         Status
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                         Expiry Date
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                         Invite Link
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                         Referral Code
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                         Actions
                       </th>
                     </tr>
                   </thead>
                   <tbody className="bg-bg-60 divide-y divide-bd-50">
                     {filteredPartners.map((partner) => (
                       <tr key={partner._id} className="hover:bg-bg-50/30">
                         <td className="px-6 py-4 whitespace-nowrap">
                           <div className="flex items-center">
                             {React.createElement(getPartnerTypeIcon(partner.type), { size: 16, className: "text-fg-60 mr-2" })}
                             <div className="text-sm font-medium text-fg-50">{partner.name}</div>
                           </div>
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                           <div className="text-sm text-fg-50 capitalize">{partner.type}</div>
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                           <div className="text-sm text-fg-50">
                             {formatCurrency(partner.rewardValue)} <span className="text-xs text-fg-60">(fixed)</span>
                           </div>
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                           <div className="text-sm text-fg-50">
                             {partner.userBonus?.type} ({partner.userBonus?.value})
                           </div>
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                           <div className="text-sm text-fg-50">{partner.totalSignuplimit}</div>
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                           <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                             partner.isExpired
                               ? 'bg-red-100 text-red-800'
                               : 'bg-green-100 text-green-800'
                           }`}>
                             {partner.isExpired ? 'Expired' : 'Active'}
                           </span>
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                           <div className="text-sm text-fg-50">
                             {partner.expirationDate ? (
                               <>
                                 <div>{new Date(partner.expirationDate).toLocaleDateString('en-US', { 
                                   year: 'numeric', 
                                   month: 'short', 
                                   day: 'numeric' 
                                 })}</div>
                                 <div className="text-xs text-fg-60">
                                   {new Date(partner.expirationDate).toLocaleTimeString('en-US', { 
                                     hour: '2-digit', 
                                     minute: '2-digit',
                                     hour12: true
                                   })}
                                 </div>
                               </>
                             ) : (
                               <span className="text-fg-60">No expiry</span>
                             )}
                           </div>
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                           <div className="flex items-center text-sm text-fg-50">
                             <Link size={16} className="text-fg-60 mr-2" />
                             <a
                               href={partner.url}
                               target="_blank"
                               rel="noopener noreferrer"
                               className="text-ac-02 hover:text-ac-01 truncate max-w-[150px] mr-2"
                             >
                               {partner.url}
                             </a>
                             <button
                               onClick={() => {
                                 navigator.clipboard.writeText(partner.url);
                                 toast.success('URL copied to clipboard');
                               }}
                               className="p-1 rounded hover:bg-bg-40"
                               title="Copy URL"
                             >
                               <Copy size={14} className="text-fg-60" />
                             </button>
                           </div>
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                           <div className="flex items-center text-sm text-fg-50">
                             <span className="font-mono text-xs bg-bg-40 px-2 py-1 rounded mr-2">
                               {partner.referralCode}
                             </span>
                             <button
                               onClick={() => {
                                 navigator.clipboard.writeText(partner.referralCode);
                                 toast.success('Referral code copied to clipboard');
                               }}
                               className="p-1 rounded hover:bg-bg-40"
                               title="Copy Referral Code"
                             >
                               <Copy size={14} className="text-fg-60" />
                             </button>
                           </div>
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                           <div className="flex items-center gap-2">
                             <button
                               className="p-1.5 rounded hover:bg-bg-40"
                               title="Edit Partner"
                               onClick={() => handleEditPartner(partner)}
                             >
                               <Pencil size={16} />
                             </button>
                             <button
                               className="p-1.5 rounded hover:bg-bg-40 group"
                               title="Delete Partner"
                               onClick={() => handleDeletePartner(partner._id)}
                             >
                               <Trash2 size={16} className="text-fg-50 group-hover:text-red-600 transition-colors" />
                             </button>
                           </div>
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>

               {filteredPartners.length === 0 && (
                 <div className="px-6 py-8 text-center">
                   <div className="text-fg-60 text-sm">No partners found</div>
                 </div>
               )}
             </div>

             {/* Pagination */}
             {!loading && filteredPartners.length > 0 && totalPages > 1 && (
               <div className="flex justify-center items-center gap-1 mt-4">
                 <button
                   className="p-1.5 rounded hover:bg-bg-40 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                   onClick={() => setCurrentPage(currentPage - 1)}
                   disabled={currentPage === 1 || loading}
                   aria-label="Previous page"
                 >
                   <ChevronLeft size={20} />
                 </button>
                 
                 {(() => {
                   const pages = [];
                   const current = currentPage;
                   
                   // Always show first page
                   pages.push(1);
                   
                   if (totalPages <= 7) {
                     // Show all pages if 7 or less
                     for (let i = 2; i <= totalPages; i++) {
                       pages.push(i);
                     }
                   } else {
                     // Show ellipsis logic
                     if (current <= 3) {
                       // Near start: 1 2 3 4 ... last
                       for (let i = 2; i <= 4; i++) pages.push(i);
                       pages.push('...');
                       pages.push(totalPages);
                     } else if (current >= totalPages - 2) {
                       // Near end: 1 ... n-3 n-2 n-1 n
                       pages.push('...');
                       for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
                     } else {
                       // Middle: 1 ... current-1 current current+1 ... last
                       pages.push('...');
                       pages.push(current - 1);
                       pages.push(current);
                       pages.push(current + 1);
                       pages.push('...');
                       pages.push(totalPages);
                     }
                   }
                   
                   return pages.map((page, idx) => 
                     page === '...' ? (
                       <span key={`ellipsis-${idx}`} className="px-3 py-1.5 text-fg-60">...</span>
                     ) : (
                       <button
                         key={page}
                         className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                           page === current 
                             ? 'bg-ac-02 text-white' 
                             : 'hover:bg-bg-40 text-fg-60'
                         }`}
                         onClick={() => setCurrentPage(page)}
                         disabled={loading}
                       >
                         {page}
                       </button>
                     )
                   );
                 })()}
                 
                 <button
                   className="p-1.5 rounded hover:bg-bg-40 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                   onClick={() => setCurrentPage(currentPage + 1)}
                   disabled={currentPage === totalPages || loading}
                   aria-label="Next page"
                 >
                   <ChevronRight size={20} />
                 </button>
               </div>
             )}
           </>
         )}

        {/* Requests Tab Content */}
        {activeTab === "requests" && (
          <>
            {/* Search */}
            <div className="flex items-end gap-3 mb-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-fg-60 mb-1">Search</label>
                <input
                  type="text"
                  className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                  placeholder="Search by name, email, or organization..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Status Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                className={`px-3 py-1 text-xs rounded-md ${
                  filterType === 'all' ? 'bg-ac-02 text-white' : 'bg-bg-60 text-fg-60'
                }`}
                onClick={() => setFilterType('all')}
              >
                All ({filteredRequests.length})
              </button>
              <button
                className={`px-3 py-1 text-xs rounded-md flex items-center gap-1 ${
                  filterType === 'pending' ? 'bg-ac-02 text-white' : 'bg-bg-60 text-fg-60'
                }`}
                onClick={() => setFilterType('pending')}
              >
                <Clock size={12} />
                Pending ({pendingRequests.length})
              </button>
              <button
                className={`px-3 py-1 text-xs rounded-md flex items-center gap-1 ${
                  filterType === 'approved' ? 'bg-ac-02 text-white' : 'bg-bg-60 text-fg-60'
                }`}
                onClick={() => setFilterType('approved')}
              >
                <CheckCircle size={12} />
                Approved ({approvedRequests.length})
              </button>
              <button
                className={`px-3 py-1 text-xs rounded-md flex items-center gap-1 ${
                  filterType === 'rejected' ? 'bg-ac-02 text-white' : 'bg-bg-60 text-fg-60'
                }`}
                onClick={() => setFilterType('rejected')}
              >
                <XCircle size={12} />
                Rejected ({rejectedRequests.length})
              </button>
            </div>

            {/* Requests Table */}
            <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-bd-50">
                  <thead className="bg-bg-40">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase">Organization</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase">Submitted</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-bg-60 divide-y divide-bd-50">
                    {(filterType === 'all' ? filteredRequests : 
                      filterType === 'pending' ? pendingRequests :
                      filterType === 'approved' ? approvedRequests : rejectedRequests).map((request) => (
                      <tr key={request._id} className="hover:bg-bg-50/30">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-fg-50">{request.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-fg-50 capitalize">{request.type}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-fg-50">{request.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-fg-50">{request.organization || "-"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            request.status === 'approved' ? 'bg-green-100 text-green-800' :
                            request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {request.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-fg-50">
                            {new Date(request.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {request.status === 'pending' && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openApprovalModal(request)}
                                className="p-1.5 rounded hover:bg-green-100 text-green-600"
                                title="Approve"
                              >
                                <CheckCircle size={16} />
                              </button>
                              <button
                                onClick={() => openRejectionModal(request)}
                                className="p-1.5 rounded hover:bg-red-100 text-red-600"
                                title="Reject"
                              >
                                <XCircle size={16} />
                              </button>
                            </div>
                          )}
                          <button
                            onClick={() => openDetailsModal(request)}
                            className="text-xs text-ac-02 hover:text-ac-01"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredRequests.length === 0 && (
                <div className="px-6 py-8 text-center">
                  <div className="text-fg-60 text-sm">No partner requests found</div>
                </div>
              )}
            </div>
          </>
        )}

        {/* All Partners Tab Content */}
        {activeTab === "analytics" && (
          <>
            {/* Search and Filter Controls */}
            <div className="flex items-end gap-3 mb-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-fg-60 mb-1">Search</label>
                <input
                  type="text"
                  className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {/* Type Filter */}
              <div>
                <label className="block text-xs font-medium text-fg-60 mb-1">Type</label>
                <select
                  className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                  value={partnerTypeFilter}
                  onChange={(e) => setPartnerTypeFilter(e.target.value)}
                >
                  <option value="">All Types</option>
                  {partnerTypeOptions.map(type => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>
              {/* Status Filter */}
               <div>
                 <label className="block text-xs font-medium text-fg-60 mb-1">Status</label>
                 <select
                   className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                   value={partnerTypeFilter}
                   onChange={(e) => setPartnerTypeFilter(e.target.value)}
                 >
                   <option value="">All</option>
                   <option value="active">Active</option>
                   <option value="inactive">Inactive</option>
                 </select>
               </div>
            </div>

            {/* Analytics Partners Table */}
            <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-bd-50">
                  <thead className="bg-bg-40">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Partner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Signups
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Total Earnings
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Balance Due
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Total Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-bg-60 divide-y divide-bd-50">
                    {filteredPartners.map((partner) => (
                      <tr key={partner._id} className="hover:bg-bg-50/30">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {React.createElement(getPartnerTypeIcon(partner.type), { size: 16, className: "text-fg-60 mr-2" })}
                            <div>
                              <div className="text-sm font-medium text-fg-50">{partner.name}</div>
                              <div className="text-xs text-fg-60 capitalize">{partner.type}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-fg-50">
                            <div className="font-medium">{partner.CurrentSignup || 0}</div>
                            <div className="text-xs text-fg-60">of {partner.totalSignuplimit} limit</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-green-600">
                            {formatCurrency(partner.totalEarnings || 0)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-medium ${
                            (partner.balanceDue || 0) > 0 ? 'text-red-600' : 'text-fg-50'
                          }`}>
                            {formatCurrency(partner.balanceDue || 0)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-fg-50">
                            {formatCurrency(partner.totalAmount || 0)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            partner.isExpired
                              ? 'bg-red-100 text-red-800'
                              : partner.CurrentSignup >= partner.totalSignuplimit
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {partner.isExpired
                              ? 'Expired'
                              : partner.CurrentSignup >= partner.totalSignuplimit
                              ? 'Full'
                              : 'Active'
                            }
                          </span>
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredPartners.length === 0 && (
                <div className="px-6 py-8 text-center">
                  <div className="text-fg-60 text-sm">No partners found</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Add/Update Partner Modal */}
      <AddOrUpdateModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingPartner(null);
        }}
        partner={editingPartner}
        onSuccess={handleModalSuccess}
      />

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-60 rounded-[22px] shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-bd-50">
              <h2 className="text-lg font-semibold text-fg-50">Approve Partner Request</h2>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedRequest(null);
                }}
                className="p-1 hover:bg-bg-40 rounded transition-colors"
              >
                <X size={20} className="text-fg-60" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-bg-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-fg-50 mb-2">Request Details</h3>
                <div className="space-y-1 text-sm text-fg-60">
                  <p><strong>Name:</strong> {selectedRequest.name}</p>
                  <p><strong>Type:</strong> {selectedRequest.type}</p>
                  <p><strong>Email:</strong> {selectedRequest.email}</p>
                  {selectedRequest.organization && <p><strong>Organization:</strong> {selectedRequest.organization}</p>}
                  {selectedRequest.reason && <p><strong>Reason:</strong> {selectedRequest.reason}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Partner Reward (Fixed Amount) */}
                <div>
                  <label className="block text-xs font-medium text-fg-60 mb-1">Partner Reward (Fixed Amount USD) *</label>
                  <input
                    type="number"
                    value={approvalData.rewardValue}
                    onChange={(e) => setApprovalData({...approvalData, rewardValue: e.target.value})}
                    className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50"
                    placeholder="5.00"
                    min="0"
                    step="0.01"
                    required
                  />
                  <p className="text-xs text-fg-70 mt-1">Fixed amount per monthly subscription</p>
                </div>

                {/* User Reward Type */}
                <div>
                  <label className="block text-xs font-medium text-fg-60 mb-1">User Reward Type</label>
                  <select
                    value={approvalData.userBonus.type}
                    onChange={(e) => setApprovalData({...approvalData, userBonus: {...approvalData.userBonus, type: e.target.value, value: e.target.value === 'none' ? '' : approvalData.userBonus.value}})}
                    className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50"
                  >
                    <option value="none">No Reward</option>
                    <option value="flat">Fixed Amount (USD)</option>
                  </select>
                </div>

                {/* User Reward Value (conditional) */}
                {approvalData.userBonus.type !== 'none' && (
                  <div>
                    <label className="block text-xs font-medium text-fg-60 mb-1">
                      {approvalData.userBonus.type === 'flat' ? 'Fixed Amount (USD)' : 'Discount (%)'}
                    </label>
                    <input
                      type="number"
                      value={approvalData.userBonus.value}
                      onChange={(e) => setApprovalData({...approvalData, userBonus: {...approvalData.userBonus, value: e.target.value}})}
                      className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50"
                      placeholder={approvalData.userBonus.type === 'flat' ? "5.00" : "30"}
                      min="0"
                      step={approvalData.userBonus.type === 'flat' ? "0.01" : "1"}
                      max={approvalData.userBonus.type === 'percentage' ? "100" : undefined}
                    />
                    <p className="text-xs text-fg-70 mt-1">Applied only on first billing period</p>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-fg-60 mb-1">Total Signup Limit</label>
                  <input
                    type="number"
                    value={approvalData.totalSignuplimit}
                    onChange={(e) => setApprovalData({...approvalData, totalSignuplimit: e.target.value})}
                    className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50"
                    placeholder="1000 (0 for unlimited)"
                    min="0"
                  />
                  <p className="text-xs text-fg-70 mt-1">Enter 0 for unlimited signups, or a fixed number (e.g., 1000)</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-fg-60 mb-1">
                    Referral Code <span className="text-xs text-fg-60">(Optional - leave empty for auto-generation)</span>
                  </label>
                  <input
                    type="text"
                    value={approvalData.referralCode}
                    onChange={(e) => setApprovalData({...approvalData, referralCode: e.target.value.toUpperCase()})}
                    className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50"
                    placeholder="Leave empty for auto-generation"
                  />
                  <p className="text-xs text-fg-70 mt-1">Each partner must have a unique referral code. Leave empty to auto-generate.</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-fg-60 mb-1">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full px-4 py-2 bg-bg-50 border-0 rounded-lg text-fg-50 resize-none"
                  rows={3}
                  placeholder="Optional notes about this approval..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-bd-50">
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedRequest(null);
                  }}
                  className="px-4 py-2 text-sm rounded-md bg-bg-40 text-fg-60 hover:bg-bg-30 transition-colors"
                  disabled={approvalLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleApproveRequest}
                  disabled={approvalLoading}
                  className="px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approvalLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Approving...
                    </>
                  ) : (
                    "Approve & Create Partner"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite via Email Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-60 rounded-[22px] shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-bd-50">
              <h2 className="text-lg font-semibold text-fg-50">Invite Partner via Email</h2>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail("");
                }}
                className="p-1 hover:bg-bg-40 rounded transition-colors"
              >
                <X size={20} className="text-fg-60" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-bg-50 p-4 rounded-lg">
                <p className="text-sm text-fg-60 mb-2">
                  Send an invitation email with a link to the partner application form. The recipient will be able to submit a partner request, which you can then review and approve.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-fg-60 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                  placeholder="partner@example.com"
                  disabled={inviteLoading}
                  required
                />
                <p className="text-xs text-fg-70 mt-1">Enter the email address of the potential partner</p>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-bd-50">
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail("");
                  }}
                  className="px-4 py-2 text-sm rounded-md bg-bg-40 text-fg-60 hover:bg-bg-30 transition-colors"
                  disabled={inviteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvitation}
                  disabled={inviteLoading || !inviteEmail.trim()}
                  className="px-4 py-2 text-sm rounded-md bg-ac-02 text-white dark:text-black hover:bg-ac-01 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviteLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Send Invitation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-60 rounded-[22px] shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-bd-50">
              <h2 className="text-lg font-semibold text-fg-50">Partner Request Details</h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedRequest(null);
                }}
                className="p-1 hover:bg-bg-40 rounded transition-colors"
              >
                <X size={20} className="text-fg-60" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                  selectedRequest.status === 'approved' ? 'bg-green-100 text-green-800' :
                  selectedRequest.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {selectedRequest.status?.toUpperCase() || 'PENDING'}
                </span>
                <span className="text-xs text-fg-60">
                  Submitted: {new Date(selectedRequest.createdAt).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>

              {/* Request Information */}
              <div className="bg-bg-50 p-4 rounded-lg space-y-3">
                <h3 className="text-sm font-semibold text-fg-50 mb-3 border-b border-bd-50 pb-2">Request Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-fg-60 block mb-1">Name</label>
                    <p className="text-sm text-fg-50">{selectedRequest.name || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-fg-60 block mb-1">Type</label>
                    <p className="text-sm text-fg-50 capitalize">{selectedRequest.type || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-fg-60 block mb-1">Email</label>
                    <p className="text-sm text-fg-50">{selectedRequest.email || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-fg-60 block mb-1">Organization/Company</label>
                    <p className="text-sm text-fg-50">{selectedRequest.organization || selectedRequest.company || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Message/Reason */}
              {(selectedRequest.message || selectedRequest.reason) && (
                <div className="bg-bg-50 p-4 rounded-lg">
                  <label className="text-xs font-medium text-fg-60 block mb-2">
                    {selectedRequest.message ? 'Message' : 'Reason'}
                  </label>
                  <p className="text-sm text-fg-50 whitespace-pre-wrap">
                    {selectedRequest.message || selectedRequest.reason || '-'}
                  </p>
                </div>
              )}

              {/* Admin Notes (if any) */}
              {selectedRequest.adminNotes && (
                <div className="bg-bg-50 p-4 rounded-lg">
                  <label className="text-xs font-medium text-fg-60 block mb-2">Admin Notes</label>
                  <p className="text-sm text-fg-50 whitespace-pre-wrap">{selectedRequest.adminNotes}</p>
                </div>
              )}

              {/* Approval/Rejection Info */}
              {selectedRequest.status !== 'pending' && (
                <div className="bg-bg-50 p-4 rounded-lg space-y-2">
                  <label className="text-xs font-medium text-fg-60 block mb-2">
                    {selectedRequest.status === 'approved' ? 'Approval' : 'Rejection'} Information
                  </label>
                  {selectedRequest.approvedAt && (
                    <p className="text-sm text-fg-50">
                      <strong>Date:</strong> {new Date(selectedRequest.approvedAt).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                  {selectedRequest.rejectedAt && (
                    <p className="text-sm text-fg-50">
                      <strong>Date:</strong> {new Date(selectedRequest.rejectedAt).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                  {selectedRequest.approvedBy && (
                    <p className="text-sm text-fg-50">
                      <strong>Processed by:</strong> Admin
                    </p>
                  )}
                </div>
              )}

              {/* Action Buttons for Pending Requests */}
              {selectedRequest.status === 'pending' && (
                <div className="flex justify-end gap-3 pt-4 border-t border-bd-50">
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openApprovalModal(selectedRequest);
                    }}
                    className="px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <CheckCircle size={16} />
                    Approve Request
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openRejectionModal(selectedRequest);
                    }}
                    className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <XCircle size={16} />
                    Reject Request
                  </button>
                </div>
              )}

              {/* Close Button for Non-Pending Requests */}
              {selectedRequest.status !== 'pending' && (
                <div className="flex justify-end pt-4 border-t border-bd-50">
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedRequest(null);
                    }}
                    className="px-4 py-2 text-sm rounded-md bg-bg-40 text-fg-60 hover:bg-bg-30 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-60 rounded-[22px] shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-bd-50">
              <h2 className="text-lg font-semibold text-fg-50">Reject Partner Request</h2>
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setSelectedRequest(null);
                }}
                className="p-1 hover:bg-bg-40 rounded transition-colors"
              >
                <X size={20} className="text-fg-60" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-bg-50 p-4 rounded-lg">
                <p className="text-sm text-fg-60">
                  Are you sure you want to reject the partner request from <strong>{selectedRequest.name}</strong>?
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-fg-60 mb-1">Rejection Reason (Optional)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full px-4 py-2 bg-bg-50 border-0 rounded-lg text-fg-50 resize-none"
                  rows={3}
                  placeholder="Optional reason for rejection..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-bd-50">
                <button
                  onClick={() => {
                    setShowRejectionModal(false);
                    setSelectedRequest(null);
                  }}
                  className="px-4 py-2 text-sm rounded-md bg-bg-40 text-fg-60 hover:bg-bg-30 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectRequest}
                  className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Reject Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Partner Confirmation Modal */}
      {showDeleteModal && partnerToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-60 rounded-[22px] shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-bd-50">
              <h2 className="text-lg font-semibold text-fg-50">Delete Partner</h2>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setPartnerToDelete(null);
                }}
                className="p-1 hover:bg-bg-40 rounded transition-colors"
                disabled={deleteLoading}
              >
                <X size={20} className="text-fg-60" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-bg-50 p-4 rounded-lg">
                <p className="text-sm text-fg-60 mb-4">
                  Are you sure you want to delete this partner? This action cannot be undone.
                </p>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-medium text-fg-60">Partner Name:</span>
                    <p className="text-sm text-fg-50 font-medium">{partnerToDelete.name}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-fg-60">Type:</span>
                    <p className="text-sm text-fg-50 capitalize">{partnerToDelete.type}</p>
                  </div>
                  {partnerToDelete.referralCode && (
                    <div>
                      <span className="text-xs font-medium text-fg-60">Referral Code:</span>
                      <p className="text-sm text-fg-50 font-mono">{partnerToDelete.referralCode}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-500 mb-1">
                      Warning: This action cannot be undone
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-400">
                      Deleting this partner will permanently remove:
                    </p>
                    <ul className="text-xs text-yellow-700 dark:text-yellow-400 mt-2 space-y-1 ml-4 list-disc">
                      <li>All associated users created under this partner</li>
                      <li>Referral codes and signup tracking data</li>
                      <li>Partner earnings and payment history</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-bd-50">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setPartnerToDelete(null);
                  }}
                  className="px-4 py-2 text-sm rounded-md bg-bg-40 text-fg-60 hover:bg-bg-30 transition-colors"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeletePartner}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete Partner
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerApplication;